/**
 * Typed fetch client for the Hive API. Calls are same-origin (Vite proxy in dev,
 * Caddy in prod), so cookies are sent automatically with `credentials: include`.
 * The CSRF token (readable cookie) is echoed in `X-CSRF-Token` on mutations.
 */

export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  correlationId?: string;
  fields?: Record<string, string[]>;
  form?: string[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly problem?: Problem,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match && match[1] !== undefined ? decodeURIComponent(match[1]) : null;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

interface RequestOpts {
  idempotencyKey?: string;
  signal?: AbortSignal;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOpts = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (UNSAFE.has(method)) {
    const csrf = readCookie("hive.csrf");
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

  const res = await fetch(path, {
    method,
    headers,
    credentials: "include",
    signal: opts.signal,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const problem = (data ?? {}) as Problem;
    throw new ApiError(res.status, problem.code ?? "error", problem.detail ?? res.statusText, problem);
  }
  return data as T;
}

/**
 * Human-readable message for an ApiError. Validation failures (422) carry
 * per-field details in the problem document — fold those in so the user sees
 * *which* field was rejected instead of a generic "Request validation failed".
 */
export function apiErrorMessage(err: ApiError): string {
  const { fields, form } = err.problem ?? {};
  const parts: string[] = [];
  if (fields) {
    for (const [name, msgs] of Object.entries(fields)) {
      if (msgs.length) parts.push(`${name}: ${msgs.join(", ")}`);
    }
  }
  if (form?.length) parts.push(...form);
  return parts.length ? parts.join(" · ") : err.message;
}

/** Ensure the CSRF cookie exists before the first mutation. */
let csrfReady: Promise<unknown> | null = null;
export function ensureCsrf(): Promise<unknown> {
  if (!csrfReady) csrfReady = request("GET", "/api/auth/csrf").catch(() => null);
  return csrfReady;
}

export const api = {
  get: <T>(path: string, opts?: RequestOpts) => request<T>("GET", path, undefined, opts),
  post: async <T>(path: string, body?: unknown, opts?: RequestOpts) => {
    await ensureCsrf();
    return request<T>("POST", path, body, opts);
  },
  put: async <T>(path: string, body?: unknown, opts?: RequestOpts) => {
    await ensureCsrf();
    return request<T>("PUT", path, body, opts);
  },
  patch: async <T>(path: string, body?: unknown, opts?: RequestOpts) => {
    await ensureCsrf();
    return request<T>("PATCH", path, body, opts);
  },
  del: async <T>(path: string, opts?: RequestOpts) => {
    await ensureCsrf();
    return request<T>("DELETE", path, undefined, opts);
  },
};

export function idempotencyKey(): string {
  return (
    (crypto as { randomUUID?: () => string }).randomUUID?.() ??
    `idk-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}
